// ---------------------------------------------------------------------------
// Head Builder
// ---------------------------------------------------------------------------
// Builds the full <head> section for a rendered page.

import type { RenderInput } from "./types.js";
import { buildStyleBlock } from "./css-cascade.js";
import { buildSeoMeta, buildJsonLd, buildWebPageSchema, buildBreadcrumbSchema, type BreadcrumbItem } from "./seo.js";
import { attr, text } from "./escape.js";
import { buildFontLinks } from "./font-optimizer.js";
import { generateSchemaOrg } from "../seo/schema-org-generator.js";
import {
  buildViewTransitionStyleTag,
  shouldEnableViewTransitions,
  VIEW_TRANSITION_META,
} from "./view-transitions.js";

// ---------------------------------------------------------------------------
// Head builder
// ---------------------------------------------------------------------------

const JSON_LD_SINGLETON_TYPES = new Set([
  "WebPage",
  "FAQPage",
  "HowTo",
  "BreadcrumbList",
  "Event",
]);

function flattenPuckComponents(input: RenderInput): Array<{ type: string; props: Record<string, unknown> }> {
  const zoneComponents = Object.values(input.puckData.zones ?? {}).flatMap((components) => components);
  return [...(input.puckData.content ?? []), ...zoneComponents];
}

function readSchemaType(schema: Record<string, unknown>): string | null {
  const typeValue = schema["@type"];
  return typeof typeValue === "string" && typeValue.length > 0 ? typeValue : null;
}

function buildBreadcrumbItems(pageUrl: string, pageTitle: string): BreadcrumbItem[] {
  try {
    const parsed = new URL(pageUrl);
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments.length === 0) {
      return [];
    }

    const items: BreadcrumbItem[] = [{
      position: 1,
      name: "Home",
      item: `${parsed.origin}/`,
    }];

    let currentPath = "";
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      items.push({
        position: index + 2,
        name: index === segments.length - 1
          ? pageTitle
          : segment
              .split("-")
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(" "),
        item: `${parsed.origin}${currentPath}`,
      });
    });

    return items;
  } catch {
    return [];
  }
}

export function buildHead(input: RenderInput): string {
  const { pageTitle, seoMeta, settings, siteContext } = input;
  const parts: string[] = [];
  const smoothScrollCss = `<style>html{scroll-behavior:smooth}@media (prefers-reduced-motion: reduce){html{scroll-behavior:auto}}</style>`;

  // 1. Charset
  parts.push('<meta charset="utf-8">');

  // 2. Viewport
  parts.push(
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
  );
  parts.push(smoothScrollCss);

  // 3. Title
  const title = seoMeta.title ?? pageTitle;
  parts.push(`<title>${text(title)}</title>`);

  // 4. SEO meta tags
  const seoTags = buildSeoMeta(pageTitle, seoMeta, seoMeta.canonicalUrl);
  if (seoTags) parts.push(seoTags);

  // 5. Favicon — page-level `settings.favicon` wins; otherwise fall back to
  //    the tenant's brand iconUrl from `workspaceBrandSettings.iconUrl`.
  //    Both values are HTML-escaped via `attr()`.
  const faviconUrl = settings.favicon ?? siteContext?.brandIconUrl ?? null;
  if (faviconUrl) {
    parts.push(`<link rel="icon" href="${attr(faviconUrl)}">`);
  }

  // 6. Google Fonts
  const fontLinks = buildFontLinks(input);
  if (fontLinks) parts.push(fontLinks);

  // 7. Style block
  const styleBlock = buildStyleBlock(
    siteContext?.globalTheme,
    siteContext?.globalTheme.customCss,
    settings.customCss,
    typeof settings.mood === "string" ? settings.mood : undefined,
  );
  parts.push(styleBlock);

  // 8. JSON-LD
  const webPageSchema = buildWebPageSchema(
    title,
    seoMeta.description,
    seoMeta.canonicalUrl,
  );
  const generatedSchema = generateSchemaOrg(
    flattenPuckComponents(input),
    seoMeta.canonicalUrl ?? "",
  );
  const breadcrumbItems = seoMeta.canonicalUrl
    ? buildBreadcrumbItems(seoMeta.canonicalUrl, title)
    : [];
  const autoSchemas: Record<string, unknown>[] = [...generatedSchema.schemas];
  if (breadcrumbItems.length > 1) {
    autoSchemas.push(buildBreadcrumbSchema(breadcrumbItems));
  }

  const explicitSchemas = Array.isArray(seoMeta.jsonLd) ? seoMeta.jsonLd : [];
  const explicitSingletonTypes = new Set(
    explicitSchemas
      .map((schema) => readSchemaType(schema))
      .filter((type): type is string => Boolean(type)),
  );

  const jsonLdSchemas: Record<string, unknown>[] = [webPageSchema];
  for (const schema of autoSchemas) {
    const type = readSchemaType(schema);
    if (type && JSON_LD_SINGLETON_TYPES.has(type) && explicitSingletonTypes.has(type)) {
      continue;
    }
    jsonLdSchemas.push(schema);
  }
  if (explicitSchemas.length > 0) {
    jsonLdSchemas.push(...explicitSchemas);
  }
  const jsonLdOutput = buildJsonLd(jsonLdSchemas);
  if (jsonLdOutput) parts.push(jsonLdOutput);

  // 8B. Native View Transitions for multi-page sites
  if (shouldEnableViewTransitions(input)) {
    parts.push(VIEW_TRANSITION_META);
    parts.push(buildViewTransitionStyleTag(input));
  }

  // 9-10. Tenant head scripts (injected verbatim — intentional)
  // TRUST BOUNDARY: These are set by admin-role users via the API.
  // Validated by pageSettingsSchema (blocks iframe/object/embed/form/base tags).
  // CSP COVERAGE: computeAllScriptHashes() runs on the full HTML AFTER rendering,
  // so inline <script> tags in these blocks are hashed and included in the CSP header.
  // External script domains must be in the tenant's allowedScriptDomains config.
  if (siteContext?.headScripts) {
    parts.push(siteContext.headScripts);
  }
  if (settings.headScripts) {
    parts.push(settings.headScripts);
  }

  return parts.join("\n");
}
